import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { ParticleRain } from '../components/ParticleRain';
import { Sticker } from '../components/Sticker';
import { birthdayConfig } from '../config';
import { useBirthdayAudio } from '../hooks/useBirthdayAudio';

const KISS_EMOJI = String.fromCodePoint(0x1f48b);
const HEART_EMOJI = String.fromCodePoint(0x1f496);

type EffectName = 'no' | 'yes' | 'kiss';

const EFFECT_PATHS: Record<EffectName, string> = {
  no: '/audio/love-no-funny.mp3',
  yes: '/audio/love-yes-happy.mp3',
  kiss: '/audio/love-kiss.mp3',
};

const EFFECT_VOLUMES: Record<EffectName, number> = {
  no: 0.92,
  yes: 0.78,
  kiss: 0.9,
};

const DUCK_DURATIONS: Record<EffectName, number> = {
  no: 1500,
  yes: 5400,
  kiss: 1500,
};

export function LoveGameSection() {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const yesRef = useRef<HTMLButtonElement | null>(null);
  const noRef = useRef<HTMLButtonElement | null>(null);
  const effectAudioRef = useRef<Partial<Record<EffectName, HTMLAudioElement>>>({});
  const volumeRestoreTimerRef = useRef<number | undefined>(undefined);
  const originalBackgroundVolumeRef = useRef<number | null>(null);
  const lastEscapeAt = useRef(0);

  const [yes, setYes] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [noPosition, setNoPosition] = useState({ x: 36, y: 248 });
  const [message, setMessage] = useState('');
  const [kissVisible, setKissVisible] = useState(false);

  const { volume, setVolume } = useBirthdayAudio();

  useEffect(() => {
    const effects = (Object.keys(EFFECT_PATHS) as EffectName[]).reduce(
      (collection, name) => {
        const audio = new Audio(EFFECT_PATHS[name]);
        audio.preload = 'auto';
        audio.volume = EFFECT_VOLUMES[name];
        collection[name] = audio;
        return collection;
      },
      {} as Record<EffectName, HTMLAudioElement>,
    );

    effectAudioRef.current = effects;

    return () => {
      window.clearTimeout(volumeRestoreTimerRef.current);

      Object.values(effects).forEach((audio) => {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      });
    };
  }, []);

  const restoreBackgroundVolume = useCallback(() => {
    const originalVolume = originalBackgroundVolumeRef.current;

    if (originalVolume !== null) {
      setVolume(originalVolume);
    }

    originalBackgroundVolumeRef.current = null;
  }, [setVolume]);

  const playEffect = useCallback(
    (name: EffectName) => {
      const effect = effectAudioRef.current[name];

      if (!effect) return;

      if (originalBackgroundVolumeRef.current === null) {
        originalBackgroundVolumeRef.current = volume;
      }

      setVolume(Math.min(volume, 0.08));
      window.clearTimeout(volumeRestoreTimerRef.current);

      effect.pause();
      effect.currentTime = 0;
      effect.volume = EFFECT_VOLUMES[name];

      void effect.play().catch(() => {
        // Do not play a generated beep or browser speech fallback.
        restoreBackgroundVolume();
      });

      volumeRestoreTimerRef.current = window.setTimeout(
        restoreBackgroundVolume,
        DUCK_DURATIONS[name],
      );
    },
    [restoreBackgroundVolume, setVolume, volume],
  );

  const moveNoButton = useCallback(
    (withSound = true) => {
      if (yes) return;

      const card = cardRef.current;
      const yesButton = yesRef.current;

      if (!card) return;

      if (withSound) {
        playEffect('no');
      }

      const width = card.clientWidth;
      const height = card.clientHeight;
      const buttonWidth = Math.min(166, Math.max(138, width * 0.42));
      const buttonHeight = 50;
      const padding = 16;
      const yesRect = yesButton?.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      let next = noPosition;

      for (let index = 0; index < 18; index += 1) {
        const candidate = {
          x:
            padding +
            Math.random() *
              Math.max(1, width - buttonWidth - padding * 2),
          y:
            210 +
            Math.random() *
              Math.max(1, height - buttonHeight - 232),
        };

        const overlapsYes = yesRect
          ? candidate.x + cardRect.left < yesRect.right + 18 &&
            candidate.x + cardRect.left + buttonWidth > yesRect.left - 18 &&
            candidate.y + cardRect.top < yesRect.bottom + 18 &&
            candidate.y + cardRect.top + buttonHeight > yesRect.top - 18
          : false;

        if (!overlapsYes) {
          next = candidate;
          break;
        }
      }

      const nextAttempts = attempts + 1;

      setAttempts(nextAttempts);
      setNoPosition(next);
      setMessage(
        birthdayConfig.copy.loveGame.funnyMessages[
          (nextAttempts - 1) %
            birthdayConfig.copy.loveGame.funnyMessages.length
        ],
      );
    },
    [attempts, noPosition, playEffect, yes],
  );

  const acceptYes = () => {
    setYes(true);
    setMessage('');
    playEffect('yes');

    confetti({
      particleCount: window.innerWidth <= 760 ? 110 : 180,
      spread: 105,
      startVelocity: 42,
      origin: { y: 0.62 },
      colors: ['#ff4fa3', '#ff8ec7', '#ffd166', '#a855f7', '#ffffff'],
      disableForReducedMotion: true,
    });

    window.setTimeout(() => {
      confetti({
        particleCount: window.innerWidth <= 760 ? 34 : 70,
        angle: 60,
        spread: 65,
        origin: { x: 0, y: 0.7 },
        disableForReducedMotion: true,
      });

      confetti({
        particleCount: window.innerWidth <= 760 ? 34 : 70,
        angle: 120,
        spread: 65,
        origin: { x: 1, y: 0.7 },
        disableForReducedMotion: true,
      });
    }, 280);
  };

  const collectKiss = () => {
    setKissVisible(true);
    playEffect('kiss');
  };

  return (
    <section className="section-shell love-game-section" id="love-game">
      {yes && <ParticleRain kind="hearts" count={window.innerWidth <= 760 ? 20 : 34} />}

      <div className="page-container narrow-container">
        <motion.div
          ref={cardRef}
          className="love-game-card glass"
          onPointerMove={(event: ReactPointerEvent<HTMLDivElement>) => {
            if (event.pointerType !== 'mouse' || yes || !noRef.current) return;

            const rect = noRef.current.getBoundingClientRect();
            const distance = Math.hypot(
              event.clientX - (rect.left + rect.width / 2),
              event.clientY - (rect.top + rect.height / 2),
            );

            if (distance < 95 && Date.now() - lastEscapeAt.current > 850) {
              lastEscapeAt.current = Date.now();
              moveNoButton(true);
            }
          }}
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Sticker
            src={
              yes
                ? '/stickers/mochi-celebrate.svg'
                : '/stickers/mochi-love.svg'
            }
            alt={
              yes
                ? 'Celebrating cute cat sticker'
                : 'Cute cat asking a question'
            }
            className="game-sticker"
          />

          <AnimatePresence mode="wait">
            {!yes ? (
              <motion.div
                key="question"
                className="love-question-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -15 }}
              >
                <span className="eyebrow">
                  {birthdayConfig.copy.loveGame.eyebrow}
                </span>

                <h2>{birthdayConfig.copy.loveGame.question}</h2>
                <p>{birthdayConfig.copy.loveGame.description}</p>

                <button
                  ref={yesRef}
                  type="button"
                  className="primary-button yes-button"
                  onClick={acceptYes}
                >
                  <Heart size={19} fill="currentColor" aria-hidden="true" />
                  {birthdayConfig.copy.loveGame.yesButton}
                </button>

                <motion.button
                  ref={noRef}
                  type="button"
                  className="no-button"
                  style={{ left: 0, top: 0 }}
                  animate={{
                    x: noPosition.x,
                    y: noPosition.y,
                    rotate: attempts % 2 ? 5 : -4,
                  }}
                  transition={{
                    type: 'spring',
                    stiffness: 360,
                    damping: 18,
                  }}
                  onMouseEnter={() => moveNoButton(true)}
                  onPointerDown={(event) => {
                    if (event.pointerType !== 'mouse') {
                      event.preventDefault();
                      moveNoButton(true);
                    }
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    moveNoButton(true);
                  }}
                  aria-label="No, playful moving button"
                >
                  {
                    birthdayConfig.copy.loveGame.noLabels[
                      Math.min(
                        attempts,
                        birthdayConfig.copy.loveGame.noLabels.length - 1,
                      )
                    ]
                  }
                </motion.button>

                <div className="funny-message" aria-live="polite">
                  {message}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="accepted"
                className="yes-result"
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <Sparkles size={42} aria-hidden="true" />
                <h2>{birthdayConfig.copy.loveGame.acceptedTitle}</h2>
                <p>{birthdayConfig.copy.loveGame.acceptedText}</p>

                <button
                  type="button"
                  className="primary-button kiss-collect-button"
                  onClick={collectKiss}
                >
                  <span aria-hidden="true">{KISS_EMOJI}</span>
                  <span>Collect Your Birthday Kiss</span>
                  <span aria-hidden="true">{KISS_EMOJI}</span>
                </button>

                <AnimatePresence>
                  {kissVisible && (
                    <motion.div
                      className="kiss-burst"
                      initial={{ opacity: 0, scale: 0.5, y: 12 }}
                      animate={{
                        opacity: 1,
                        scale: [0.5, 1.16, 1],
                        y: 0,
                      }}
                      exit={{ opacity: 0, scale: 0.65 }}
                    >
                      <span>{KISS_EMOJI}</span>
                      <span>{HEART_EMOJI}</span>
                      <span>{KISS_EMOJI}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}